<template>
  <div class="editable-container">
    <i v-if="showIcon" class="pi pi-pencil pencil-icon"></i>
    <component
      :is="multiline ? 'textarea' : 'input'"
      ref="elementRef"
      :type="multiline ? undefined : 'text'"
      v-model:value="model"
      @focus="onFocus"
      @input="onInput"
      @blur="onBlur"
      @keydown="onKeyDown"
      :class="{ 'with-icon': showIcon }"
      :rows="multiline ? 1 : undefined"
    />
  </div>
</template>

<script setup>
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue'

const props = defineProps({
  showIcon: {
    type: Boolean,
    default: false,
  },
  multiline: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['focus', 'input', 'blur', 'keydown'])

const model = defineModel({ type: [String, Number, null] })
const elementRef = ref(null)

function autoResize(element) {
  if (!element) return
  element.style.height = 'auto'
  element.style.height = `${element.scrollHeight}px`
}

const onFocus = e => {
  emit('focus', e)
  if (props.multiline) {
    autoResize(e.target)
  }
}

const onInput = e => {
  emit('input', e.target.value)
  if (props.multiline) {
    autoResize(e.target)
  }
}

const onBlur = e => {
  emit('blur', e)
}

const onKeyDown = e => {
  if (e.key === 'Escape') {
    e.target.blur()
  }
  // In multiline mode, pressing Enter without Shift should blur the field
  if (props.multiline && e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    e.target.blur()
  }
  emit('keydown', e)
}

// React only to width changes: autoResize sets the element's height, which
// would re-fire the observer and loop if height changes were handled too.
let resizeObserver = null
let lastObservedWidth = 0

onMounted(() => {
  if (props.multiline && elementRef.value) {
    autoResize(elementRef.value)
    lastObservedWidth = elementRef.value.clientWidth
    resizeObserver = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width ?? 0
      if (width !== lastObservedWidth) {
        lastObservedWidth = width
        autoResize(elementRef.value)
      }
    })
    resizeObserver.observe(elementRef.value)
  }
})

onUnmounted(() => {
  resizeObserver?.disconnect()
  resizeObserver = null
})

watch(model, () => {
  if (props.multiline && elementRef.value) {
    nextTick(() => {
      autoResize(elementRef.value)
    })
  }
})
</script>

<style scoped>
  .editable-container {
    display: flex;
    align-items: center;
    position: relative;
    width: 100%;
  }

  .editable-container:has(textarea) {
    align-items: flex-start;
  }

  .pencil-icon {
    font-size: 0.7rem;
    color: #6c757d;
    z-index: 1;
    margin-right: 10px;
    align-self: center;
  }

  .editable-container:has(textarea) .pencil-icon {
    padding-top: 0.1rem;
  }

  input,
  textarea {
    outline: 0px solid transparent;
    width: 100%;
    display: inline-block;
    border: none;
    background: transparent;
    padding: 0;
    margin: 0;
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
  }

  textarea {
    resize: none;
    overflow-y: hidden;
  }

  input:focus,
  textarea:focus {
    box-shadow: none !important;
  }

  .editable-container:has(input:focus),
  .editable-container:has(textarea:focus) {
    box-shadow: inset 0 0 0 1px #07733d86 !important;
  }

  input.with-icon {
    padding-left: 10px;
  }
</style>
